import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import TextField from "@/components/TextField";
import useModal from "@/hooks/useModal";
import { showSnackbar } from "@/lib/snackbarUtils";
import { GET_ACTIVITY_PARTICIPATION } from "@/pages/activities/activity-types/queries";
import { RATE_UNIT_MAP } from "@/pages/activities/activity-types/_utils";
import type { ActivityParticipation } from "@/pages/activities/activity-types/types";
import { UPDATE_ACTIVITY_TYPE_PARTICIPATION_MUTATION } from "../mutations";
import type { Person } from "@/types";

type TypeLevelEditFormValues = {
  title: string;
  description: string;
  quantity: string;
  startDate: string;
  endDate: string;
};

type ActivityParticipationQueryData = {
  activityParticipation: ActivityParticipation | null;
};

const toDateInputValue = (value?: string | null) => value?.split("T")[0] ?? "";

const getPersonLabel = (person?: Person | null) => {
  const fullName = [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();
  return fullName || person?.pgaId || "";
};

export const isValidQuantityValue = (value: string) => {
  if (!/^\d+(\.\d)?$/.test(value)) return false;
  const numberValue = Number(value);
  if (isNaN(numberValue) || numberValue <= 0) return false;
  return true;
};

export default function TypeLevelEditParticipationModal({
  onDismiss,
  activityParticipationId,
  additionalRefetchQueries
}: {
  onDismiss: () => void;
  activityParticipationId: string;
  additionalRefetchQueries?: any[];
}) {
  const { Modal, close } = useModal();
  const [bannerError, setBannerError] = useState("");
  const { data, loading } = useQuery<ActivityParticipationQueryData>(GET_ACTIVITY_PARTICIPATION, {
    variables: { id: activityParticipationId },
    skip: !activityParticipationId
  });

  const participation = data?.activityParticipation;
  const creditRates = participation?.activityType?.config?.creditRates ?? [];
  const selectedCreditRate = creditRates.find(
    (creditRate) =>
      creditRate.attendanceType === participation?.attendanceType &&
      creditRate.participationRole === participation?.role
  );
  const rateUnit = selectedCreditRate?.rateUnit;
  const rateMultiplier = selectedCreditRate?.rateMultiplier;
  const rateUnitLabel = rateUnit ? RATE_UNIT_MAP[rateUnit] : "unit";
  const hideQuantity = rateUnit === "INSTANCE" && rateMultiplier !== 1;

  const defaultValues = useMemo<TypeLevelEditFormValues>(
    () => ({
      title: participation?.title ?? "",
      description: participation?.description ?? "",
      quantity: String(participation?.quantity ?? ""),
      startDate: toDateInputValue(participation?.beganAt),
      endDate: toDateInputValue(participation?.endedAt || participation?.beganAt)
    }),
    [participation]
  );

  const form = useForm<TypeLevelEditFormValues>({
    defaultValues,
    mode: "onChange"
  });
  const { control, watch, getValues, setValue, reset } = form;
  const resetParticipationId = useRef<string | null>(null);
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const title = watch("title");
  const quantity = watch("quantity");

  useEffect(() => {
    if (!participation?.id || resetParticipationId.current === participation.id) {
      return;
    }

    reset(defaultValues);
    resetParticipationId.current = participation.id;
  }, [defaultValues, participation?.id, reset]);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setValue("endDate", startDate);
    }
  }, [startDate, endDate, setValue]);

  const [updateParticipation, { loading: saving }] = useMutation(UPDATE_ACTIVITY_TYPE_PARTICIPATION_MUTATION, {
    refetchQueries: [
      { query: GET_ACTIVITY_PARTICIPATION, variables: { id: activityParticipationId } },
      ...(additionalRefetchQueries || [])
    ],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      const response = result?.updateActivityTypeParticipation;
      if (response?.success) {
        showSnackbar(response.message || "Participation updated");
        close();
        onDismiss();
      } else {
        setBannerError(response?.message || "The participation could not be updated.");
      }
    },
    onError: (error) => {
      setBannerError(`Error: ${error.message}`);
    }
  });

  const parsedQuantity = hideQuantity ? 1 : isValidQuantityValue(quantity) ? Number(quantity) : null;
  const canSave =
    !!participation &&
    !!selectedCreditRate &&
    !!startDate &&
    !!title.trim() &&
    (hideQuantity || parsedQuantity !== null);

  const handleSave = async () => {
    if (!canSave || parsedQuantity === null) return;

    setBannerError("");
    const effectiveEndDate = endDate || startDate;
    const trimmedDescription = getValues("description").trim();

    await updateParticipation({
      variables: {
        id: activityParticipationId,
        startDate: `${startDate}T00:00:00Z`,
        endDate: `${effectiveEndDate}T00:00:00Z`,
        quantity: parsedQuantity,
        title: title.trim(),
        description: trimmedDescription || null
      }
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!participation) {
    return (
      <Modal onClosed={onDismiss} headline="Edit Participation" className="h-fit md:w-[490px]">
        <div className="space-y-4">
          <Banner inline variant="error" description="The participation could not be loaded." />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={!canSave || saving}
            testId="type-level-edit-save-button"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
      headline="Edit Participation"
      hasFormElements
      className="h-fit max-h-[90vh] md:w-[490px]"
    >
      <div className="space-y-6">
        {!!bannerError && (
          <Banner inline variant="error" description="The participation could not be saved." subText={bannerError} />
        )}

        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Edits a participation for one person without creating an activity.
        </p>

        <TextField
          label="Person"
          value={getPersonLabel(participation.person)}
          variant="outlined"
          className="w-full"
          readOnly
          disabled
        />

        <Controller
          name="title"
          control={control}
          rules={{ required: "Title is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label="Title"
              value={value}
              onChangeText={onChange}
              required
              variant="outlined"
              className="w-full"
              error={!!error}
              errorText={error?.message}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Description"
              value={value}
              onChangeText={onChange}
              variant="outlined"
              className="w-full"
            />
          )}
        />

        {!hideQuantity && (
          <Controller
            name="quantity"
            control={control}
            rules={{
              required: "Quantity is required",
              validate: (value) => {
                if (!isValidQuantityValue(value)) return "Must be a positive number with up to one decimal place";
                return true;
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={rateUnit === "INSTANCE" ? "PDR Credits" : `Quantity (${rateUnitLabel}s)`}
                value={value}
                onChangeText={onChange}
                required
                variant="outlined"
                className="w-full"
                type="number"
                error={!!error}
                errorText={error?.message}
              />
            )}
          />
        )}

        <div className="flex gap-2">
          <Controller
            name="startDate"
            control={control}
            rules={{ required: "Start Date is required" }}
            render={({ field, fieldState }) => (
              <DateSelect
                label="Start Date *"
                value={{ startDate: field.value || "", endDate: field.value || "" }}
                useSingleDate
                useModal
                onChange={(value) => {
                  if (value === null || typeof value === "string") {
                    field.onChange("");
                    return;
                  }
                  const selected = value.startDate;
                  field.onChange(selected instanceof Date ? selected.toISOString().split("T")[0] : (selected ?? ""));
                }}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateSelect
                label="End Date"
                value={{ startDate: field.value || "", endDate: field.value || "" }}
                useSingleDate
                useModal
                minDate={startDate ? new Date(`${startDate}T00:00:00`) : undefined}
                onChange={(value) => {
                  if (value === null || typeof value === "string") {
                    field.onChange("");
                    return;
                  }
                  const selected = value.startDate;
                  field.onChange(selected instanceof Date ? selected.toISOString().split("T")[0] : (selected ?? ""));
                }}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
